import {ReactNode, useCallback} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {css, Icon, view} from '@sail/ui/deprecated';
import {warning} from '@sail/icons/react/Icon';
import {UploadButton} from './UploadButton';
import {DropFileZone} from './DropFileZone';
import {useDragAndDrop} from './useDragAndDrop';
import {Attachment} from './Attachment';
import {AttachmentPreviewList} from './AttachmentPreviewList';

export const messages = defineMessages({
  title: {
    id: 'support_conversations_client.attachments.title',
    description: 'Title of attachments section',
    defaultMessage: 'Add attachments',
  },
  description: {
    id: 'support_conversations_client.attachments.description',
    description:
      'Describes what kind of attachments are supported when adding a new attachment',
    defaultMessage:
      'You may upload jpeg, png, pdf, xml, csv, tsv, xlsx, or docx files that are less than 20 MB each.',
  },
});

type AddAttachmentProps = {
  attachments: Attachment[];
  supportedMimeTypes: string[];
  isLoading: boolean;
  uploadBatchTotal: number;
  uploadBatchCurrentIndex: number;
  error?: ReactNode;
  onFilesSelected: (files: FileList) => void;
  onRemoveAttachment: (attachment: Attachment) => void;
};

export const UploadFileControl = ({
  attachments,
  supportedMimeTypes,
  isLoading,
  uploadBatchTotal,
  uploadBatchCurrentIndex,
  error,
  onFilesSelected,
  onRemoveAttachment,
}: AddAttachmentProps): JSX.Element | null => {
  const intl = useIntl();

  const handleDragAndDropDataTransfer = useCallback(
    (dataTransfer: DataTransfer) => {
      if (isLoading) {
        return;
      }

      const {files} = dataTransfer;
      if (files && files.length > 0) {
        onFilesSelected(files);
      }
    },
    [isLoading, onFilesSelected],
  );

  const {isDragging, handleDragLeave, handleDragOver, handleDrop} =
    useDragAndDrop({onDataTransfer: handleDragAndDropDataTransfer});

  return (
    <view.div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDragEnd={handleDragLeave}
      onDrop={handleDrop}
      data-testid="upload-file-control"
    >
      <view.h2 css={{margin: 0}}>{intl.formatMessage(messages.title)}</view.h2>

      <view.div
        css={{position: css.value('relative'), minHeight: css.value('84px')}}
      >
        {isDragging && !isLoading && <DropFileZone />}
        <view.p css={{marginTop: 'xsmall', paddingTop: 'xsmall'}}>
          {intl.formatMessage(messages.description)}
        </view.p>
        {error ? (
          <view.p
            css={{
              minHeight: css.value('$space.xlarge'),
              display: css.value('flex'),
              alignItems: css.value('center'),
              font: 'body',
              color: css.value('$color.critical500'),
            }}
          >
            <>
              <Icon
                icon={warning}
                size="small"
                data-testid="warning-icon"
                css={{
                  marginRight: 150,
                  flexShrink: css.value('0'),
                }}
              />
              {error}
            </>
          </view.p>
        ) : null}
        <AttachmentPreviewList
          attachments={attachments}
          onRemoveAttachment={onRemoveAttachment}
        />
      </view.div>

      <view.div css={{marginTop: 'medium'}}>
        <UploadButton
          name="add-attachment"
          isLoading={isLoading}
          uploadBatchTotal={uploadBatchTotal}
          uploadBatchCurrentIndex={uploadBatchCurrentIndex}
          onFilesSelected={onFilesSelected}
          supportedMimeTypes={supportedMimeTypes}
        />
      </view.div>
    </view.div>
  );
};
